import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  FaArrowLeft, FaSyncAlt, FaFileExcel, FaFilePdf, FaSchool, FaRecycle, FaUserGraduate, FaLaptop, FaChalkboardTeacher,
  FaMapMarkedAlt, FaMapMarkerAlt, FaLayerGroup, FaCamera, FaSearch, FaLock, FaChevronRight, FaChevronLeft, FaSort,
  FaSortUp, FaSortDown, FaRegClock, FaTrophy, FaInfoCircle, FaExclamationTriangle, FaHourglassHalf, FaInbox, FaListUl,
  FaAngleDoubleLeft, FaAngleDoubleRight, FaImage, FaArrowUp, FaArrowDown,
} from "react-icons/fa";
import axiosConn from "config/axiosConn";
import { SC6_CAMPAIGN, SC6_NAME, SC6_TITLE, SC6_QUESTIONS } from "../../SpecialCampaign6/sc6Config";
import posterImg from "../../../assets/images/special-campaign-6-banner.jpg";
import "./Sc6Report.css";

/*
 * /admin/special-campaign-6-report — Special Campaign 6 (E-Waste Collection &
 * Responsible Disposal, 28.10.2026) report for central and state admins.
 *
 *   India → State/UT/AB → District → Block → Schools
 *
 * The area figures come from POST sc6/admin/report — a snapshot the server
 * recomputes every 10 minutes (national = one row per State/UT/AB; state =
 * one row per district plus every block of the state). District and block
 * views are computed from the state snapshot already loaded, no extra fetch.
 * The school-wise list is live: POST sc6/admin/schools (50 per page; export
 * returns every row). State admins (role 4) are locked to their own state by
 * the server; the page starts at their state and never shows India.
 *
 * Excel and PDF downloads are built from the same lists the page renders, so
 * the files carry exactly the figures on screen.
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const IST = "Asia/Kolkata"; // every date on the page and in the files is Indian time
const num = (n) => Number(n) || 0;
const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("en-IN"));
const pct = (n, d) => (d ? Math.round((num(n) / d) * 1000) / 10 : 0);
const pctText = (n, d) => (d ? `${pct(n, d)}%` : "—");
const avg = (n, d) => (d ? Math.round((num(n) / d) * 10) / 10 : 0);
const isNone = (k) => !k || k === "__none__";
const fmtStamp = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { timeZone: IST, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
};
const fmtTime = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("en-IN", { timeZone: IST, hour: "2-digit", minute: "2-digit", hour12: true }) : "";
};
const fmtDay = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("en-IN", { timeZone: IST, day: "2-digit", month: "short", year: "numeric" }) : "";
};
const dateStamp = () => new Date().toLocaleDateString("en-CA", { timeZone: IST }); // YYYY-MM-DD (IST)
const filePart = (s) => String(s || "").normalize("NFKD").replace(/[^\x20-\x7E]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
// The built-in PDF font (helvetica) only has Latin-1 glyphs.
const safe = (s) => String(s ?? "").replace(/[^\x20-\x7E\xA0-\xFF]/g, "").replace(/\s+/g, " ").trim();
// Names for the PDF: a name partly in a regional script keeps its Latin part
// (without stray leading/trailing punctuation) and is marked; a fully regional
// name is replaced by a note — the UDISE code next to it still identifies it.
const safeName = (s) => {
  const raw = String(s ?? "").trim();
  if (!raw) return "-";
  const latin = safe(raw).replace(/^[\s,.;:/|-]+|[\s,;:/|-]+$/g, "");
  if (!latin) return "(name in regional script)";
  return latin === safe(raw) && safe(raw).length === raw.replace(/\s+/g, " ").length ? latin : `${latin} (partly in regional script)`;
};
const yn = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "-");
const isAuthFailure = (e) => e?.response?.status === 401 || /please login|session expired|json web token/i.test(e?.response?.data?.message || e?.message || "");
const post = async (url, payload) => {
  const res = await axiosConn("post", url, payload);
  if (!res?.data?.success) throw new Error(res?.data?.message || "Request failed");
  return res.data;
};
const errText = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

// The campaign day as printed on the poster. Not derived from SC6_WINDOW: the
// upload window can open earlier / stay open longer than the campaign day.
const CAMPAIGN_DAY = "28 Oct 2026";
const AUTO_REFRESH_MS = 5 * 60 * 1000;
const PAGE_SIZE = 50;

// Per level: what the area table lists and what it is called.
const LEVELS = {
  national: { heading: "States/UTs/ABs-wise report", col: "States/UTs/ABs", plural: "States/UTs/ABs", one: "State/UT/AB", sheet: "States-UTs-ABs-wise" },
  state: { heading: "District-wise report", col: "District", plural: "districts", one: "district", sheet: "District-wise" },
  district: { heading: "Block-wise report", col: "Block", plural: "blocks", one: "block", sheet: "Block-wise" },
};

// Area-table measures. One list drives the table, the Excel sheets and the PDF.
const BASE_COLS = [
  { key: "schools", label: "Schools", xl: "Schools submitted", bar: "blue" },
  { key: "quizYes", label: "Quiz organised (Yes)", xl: "Quiz organised (Yes)" },
  { key: "quizPct", label: "%", xl: "Quiz organised %", share: "quizYes", title: "Share of schools that organised the quiz" },
  { key: "items", label: "E-Waste items collected", xl: "E-Waste items collected", bar: "orange" },
  { key: "disposedYes", label: "Disposed to recycler (Yes)", xl: "Disposed to recycler (Yes)" },
  { key: "disposedPct", label: "%", xl: "Disposed to recycler %", share: "disposedYes", title: "Share of schools that handed over to an authorised recycler/refurbisher" },
  { key: "students", label: "Students participated", xl: "Students participated" },
];
const colsFor = (level) => [
  ...(level === "national" ? [{ key: "districts", label: "Districts", xl: "Districts reporting" }] : []),
  ...(level === "national" || level === "state" ? [{ key: "blocks", label: "Blocks", xl: "Blocks reporting" }] : []),
  ...BASE_COLS,
];
const colValue = (r, c) => (c.share ? (num(r.schools) ? pct(r[c.share], num(r.schools)) : null) : num(r[c.key]));

const SUM_KEYS = ["schools", "quizYes", "quizNo", "items", "disposedYes", "disposedNo", "students", "photos", "districts", "blocks"];
const sumRows = (rows) => {
  const t = {};
  SUM_KEYS.forEach((k) => { t[k] = rows.reduce((a, r) => a + num(r[k]), 0); });
  return t;
};
const sortRows = (rows, sort, cols) => {
  const col = cols.find((c) => c.key === sort.key);
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (isNone(a.key) !== isNone(b.key)) return isNone(a.key) ? 1 : -1; // "Not recorded" always last
    if (sort.key === "label" || !col) return String(a.label || "").localeCompare(String(b.label || "")) * dir;
    const va = colValue(a, col) ?? -1;
    const vb = colValue(b, col) ?? -1;
    return (va - vb) * dir || String(a.label || "").localeCompare(String(b.label || ""));
  });
};
const byName = (a, b) => (isNone(a.key) !== isNone(b.key) ? (isNone(a.key) ? 1 : -1) : String(a.label || "").localeCompare(String(b.label || "")));

// The headline list of the Summary sheet.
function summaryLines(k, counts) {
  const s = num(k.schools);
  const lines = [
    ["Schools submitted", s, ""],
    ["Quiz on E-Waste in morning assembly - Yes", num(k.quizYes), pctText(k.quizYes, s)],
    ["Quiz on E-Waste in morning assembly - No", num(k.quizNo), pctText(k.quizNo, s)],
    ["No. of E-Waste items collected", num(k.items), ""],
    ["Disposed to authorised recycler/refurbisher - Yes", num(k.disposedYes), pctText(k.disposedYes, s)],
    ["Disposed to authorised recycler/refurbisher - No", num(k.disposedNo), pctText(k.disposedNo, s)],
    ["Students participated", num(k.students), ""],
    ["Photos uploaded", num(k.photos), ""],
  ];
  counts.forEach((c) => lines.push([c.label, num(c.value), ""]));
  lines.push(["Average E-Waste items per school", avg(k.items, s), ""]);
  lines.push(["Average students per school", avg(k.students, s), ""]);
  return lines;
}

// The poster, shrunk for the PDF header (cached; the PDF works without it).
let posterPromise = null;
const loadPoster = () => {
  if (!posterPromise) {
    posterPromise = new Promise((resolve) => {
      try {
        const img = new Image();
        img.onload = () => {
          try {
            const c = document.createElement("canvas");
            c.width = 480; c.height = 270;
            c.getContext("2d").drawImage(img, 0, 0, 480, 270);
            resolve(c.toDataURL("image/jpeg", 0.82));
          } catch (e) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = posterImg;
      } catch (e) { resolve(null); }
    });
  }
  return posterPromise;
};

// ─── PDF building blocks ──────────────────────────────────────────────────────
const C = {
  blue: [29, 79, 179], green: [30, 123, 52], orange: [217, 119, 6], violet: [109, 40, 217], teal: [15, 118, 110],
  ink: [20, 33, 61], muted: [91, 103, 130], line: [221, 229, 240], band: [236, 243, 255], mint: [236, 248, 240],
};
function pdfHeader(doc, { area, asOf, poster, M }) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(...C.band); doc.rect(0, 0, W, 96, "F");
  doc.setFillColor(...C.mint); doc.triangle(W * 0.45, 96, W, 0, W, 96, "F");
  doc.setFillColor(...C.blue); doc.rect(0, 96, W / 3, 3.5, "F");
  doc.setFillColor(...C.green); doc.rect(W / 3, 96, W / 3, 3.5, "F");
  doc.setFillColor(...C.orange); doc.rect((W / 3) * 2, 96, W / 3 + 1, 3.5, "F");
  if (poster) {
    try { doc.addImage(poster, "JPEG", W - M - 136, 12, 136, 76.5); } catch (e) { /* header works without the poster */ }
  }
  doc.setTextColor(...C.blue); doc.setFont("helvetica", "bold"); doc.setFontSize(19);
  doc.text(`${SC6_NAME} - Report`, M, 36);
  doc.setTextColor(...C.green); doc.setFontSize(11.5);
  doc.text(SC6_TITLE, M, 54);
  doc.setTextColor(...C.ink); doc.setFont("helvetica", "normal"); doc.setFontSize(9.5);
  doc.text(`Under ${SC6_CAMPAIGN}   |   Area: ${safe(area)}   |   Event day: ${CAMPAIGN_DAY}`, M, 72);
  doc.setTextColor(...C.muted); doc.setFontSize(8.5);
  doc.text(`${asOf}   |   Downloaded ${fmtStamp(new Date())}`, M, 86);
  return 116;
}
const pdfPct = (n, d) => (d ? `${pct(n, d)}%` : "-");
function pdfKpis(doc, y, k, counts, M) {
  const W = doc.internal.pageSize.getWidth();
  const s = num(k.schools);
  const boxes = [
    { label: "Schools submitted", value: fmt(s), sub: counts.length ? `in ${fmt(counts[0].value)} ${counts[0].short}` : "", tone: C.blue },
    { label: "Quiz on E-Waste in morning assembly", value: fmt(k.quizYes), sub: `${pdfPct(k.quizYes, s)} of schools`, tone: C.green },
    { label: "No. of E-Waste items collected", value: fmt(k.items), sub: `avg ${fmt(avg(k.items, s))} per school`, tone: C.orange },
    { label: "Disposed to authorised recycler/refurbisher", value: fmt(k.disposedYes), sub: `${pdfPct(k.disposedYes, s)} of schools`, tone: C.teal },
    { label: "Students participated", value: fmt(k.students), sub: `avg ${fmt(avg(k.students, s))} per school`, tone: C.violet },
  ];
  const gap = 10;
  const h = 74;
  const bw = (W - M * 2 - gap * (boxes.length - 1)) / boxes.length;
  boxes.forEach((b, i) => {
    const x = M + i * (bw + gap);
    doc.setFillColor(247, 250, 255); doc.setDrawColor(...C.line); doc.setLineWidth(0.6);
    doc.roundedRect(x, y, bw, h, 5, 5, "FD");
    doc.setFillColor(...b.tone); doc.rect(x + 0.3, y + 0.3, bw - 0.6, 3.2, "F");
    doc.setTextColor(...b.tone); doc.setFont("helvetica", "bold"); doc.setFontSize(17);
    doc.text(String(b.value), x + 10, y + 27);
    doc.setTextColor(...C.ink); doc.setFontSize(7.8);
    doc.splitTextToSize(b.label, bw - 18).slice(0, 2).forEach((ln, j) => doc.text(ln, x + 10, y + 41 + j * 9.5));
    doc.setFont("helvetica", "normal"); doc.setTextColor(...C.muted); doc.setFontSize(7.6);
    if (b.sub) doc.text(doc.splitTextToSize(b.sub, bw - 18)[0], x + 10, y + h - 9);
  });
  // the secondary figures, one line under the boxes
  const facts = [...counts.map((c) => `${c.label}: ${fmt(c.value)}`), `Photos uploaded: ${fmt(k.photos)}`];
  doc.setFont("helvetica", "normal"); doc.setFontSize(8.6); doc.setTextColor(...C.ink);
  doc.text(facts.join("     |     "), M, y + h + 16);
  return y + h + 16;
}
function pdfSectionTitle(doc, y, title, desc, M) {
  doc.setFillColor(...C.blue); doc.rect(M, y - 10, 3.5, 14, "F");
  doc.setTextColor(...C.ink); doc.setFont("helvetica", "bold"); doc.setFontSize(12.5);
  doc.text(title, M + 10, y + 1);
  if (desc) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.2); doc.setTextColor(...C.muted);
    doc.text(desc, M + 10, y + 14);
  }
  return y + (desc ? 22 : 10);
}
const TABLE_STYLE = {
  styles: { font: "helvetica", fontSize: 8, cellPadding: 4, lineColor: C.line, lineWidth: 0.5, textColor: C.ink, valign: "middle" },
  headStyles: { fillColor: C.blue, textColor: 255, fontSize: 7.8, fontStyle: "bold", valign: "middle" },
  footStyles: { fillColor: [233, 246, 237], textColor: C.ink, fontStyle: "bold" },
  alternateRowStyles: { fillColor: [248, 251, 255] },
};
function pdfFooter(doc, M, label) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7.8); doc.setTextColor(120, 132, 155);
    doc.text(`${SC6_NAME} - ${SC6_TITLE} | ${safe(label)}`, M, H - 16);
    doc.text(`Page ${p} of ${pages}`, W - M, H - 16, { align: "right" });
  }
}

// ─── small building blocks ────────────────────────────────────────────────────
function Kpi({ tone, icon, label, value, caption, share, i, loading }) {
  return (
    <div className={`s6r-kpi s6r-tone--${tone}`} style={{ "--i": i }}>
      <div className="s6r-kpi__top">
        <span className="s6r-kpi__icon" aria-hidden="true">{icon}</span>
        <span className="s6r-kpi__label">{String(label).replace(/\//g, "/\u200b")}</span>
      </div>
      {loading ? <span className="s6r-skel s6r-skel--num" /> : <span className="s6r-kpi__value">{value}</span>}
      <div className="s6r-kpi__caption">{loading ? " " : caption}</div>
      {share != null ? <div className="s6r-kpi__meter"><i style={{ width: `${loading ? 0 : Math.min(100, share)}%` }} /></div> : null}
    </div>
  );
}

// Highest / lowest share among the areas of the current table. Areas with
// fewer than 10 schools are left out when enough larger ones exist, so a
// single-school block does not top the list with 100%.
function Extremes({ rows, yesKey, word }) {
  const all = (rows || []).filter((r) => !isNone(r.key) && num(r.schools) > 0).map((r) => ({ label: r.label, p: pct(r[yesKey], num(r.schools)), n: num(r.schools) }));
  const big = all.filter((x) => x.n >= 10);
  const list = big.length >= 3 ? big : all;
  if (list.length < 2) return null;
  const hi = list.reduce((a, b) => (b.p > a.p || (b.p === a.p && b.n > a.n) ? b : a));
  const lo = list.reduce((a, b) => (b.p < a.p || (b.p === a.p && b.n > a.n) ? b : a));
  return (
    <div className="s6r-extremes">
      <div className="s6r-extremes__row is-hi">
        <span className="s6r-extremes__tag"><FaArrowUp aria-hidden="true" />Highest</span>
        <span className="s6r-extremes__name" title={hi.label}>{hi.label}</span>
        <b>{`${hi.p}%`}</b>
      </div>
      <div className="s6r-extremes__row is-lo">
        <span className="s6r-extremes__tag"><FaArrowDown aria-hidden="true" />Lowest</span>
        <span className="s6r-extremes__name" title={lo.label}>{lo.label}</span>
        <b>{`${lo.p}%`}</b>
      </div>
      <div className="s6r-extremes__note">{`Share of schools, among ${fmt(list.length)} ${word}${list === big && big.length < all.length ? " with 10 or more schools" : ""}`}</div>
    </div>
  );
}

function SplitCard({ tone, icon, title, question, yes, no, base, loading, extremes }) {
  const y = num(yes);
  const n = num(no);
  const rest = Math.max(0, num(base) - y - n);
  const total = num(base) || y + n;
  const w = (v) => (total ? (v / total) * 100 : 0);
  return (
    <div className={`s6r-card s6r-split s6r-tone--${tone}`}>
      <div className="s6r-card__head">
        <span className="s6r-card__icon" aria-hidden="true">{icon}</span>
        <div>
          <div className="s6r-card__title">{title}</div>
          <div className="s6r-card__desc">{question}</div>
        </div>
      </div>
      {loading ? <div className="s6r-skel s6r-skel--bar" /> : (
        <>
          <div className="s6r-split__big">
            <b>{pctText(y, total)}</b>
            <span>{total ? `of ${fmt(total)} schools said Yes` : "No submissions yet"}</span>
          </div>
          <div className={`s6r-split__bar${total ? "" : " is-empty"}`} role="img" aria-label={`Yes ${fmt(y)}, No ${fmt(n)}`}>
            {y > 0 && <i className="is-yes" style={{ width: `${w(y)}%` }} />}
            {n > 0 && <i className="is-no" style={{ width: `${w(n)}%` }} />}
            {rest > 0 && <i className="is-na" style={{ width: `${w(rest)}%` }} />}
          </div>
          <div className="s6r-split__legend">
            <span><i className="is-yes" />Yes<b>{fmt(y)}</b>{total ? <small>{pctText(y, total)}</small> : null}</span>
            <span><i className="is-no" />No<b>{fmt(n)}</b>{total ? <small>{pctText(n, total)}</small> : null}</span>
            {rest > 0 && <span><i className="is-na" />Not answered<b>{fmt(rest)}</b></span>}
          </div>
          {extremes}
        </>
      )}
    </div>
  );
}

const TOP_METRICS = [["schools", "Schools"], ["items", "E-Waste items"], ["students", "Students"]];
function TopCard({ title, rows, metric, setMetric, onOpen, canOpen, loading }) {
  const list = [...rows].filter((r) => !isNone(r.key) && num(r[metric]) > 0).sort((a, b) => num(b[metric]) - num(a[metric])).slice(0, 5);
  const max = Math.max(1, ...list.map((r) => num(r[metric])));
  return (
    <div className="s6r-card s6r-top">
      <div className="s6r-card__head">
        <span className="s6r-card__icon s6r-card__icon--gold" aria-hidden="true"><FaTrophy /></span>
        <div style={{ minWidth: 0 }}>
          <div className="s6r-card__title">{title}</div>
          <div className="s6r-seg" role="group" aria-label="Rank by">
            {TOP_METRICS.map(([k, l]) => (
              <button key={k} type="button" className={metric === k ? "is-on" : ""} onClick={() => setMetric(k)}>{l}</button>
            ))}
          </div>
        </div>
      </div>
      {loading ? <div className="s6r-skel s6r-skel--list" /> : list.length === 0 ? (
        <div className="s6r-top__empty">Rankings appear as soon as schools submit.</div>
      ) : (
        <ol className="s6r-top__list">
          {list.map((r, i) => (
            <li key={r.key}>
              <button type="button" className="s6r-top__row" onClick={() => canOpen && onOpen(r)} disabled={!canOpen} title={canOpen ? `Open ${r.label}` : r.label}>
                <span className={`s6r-rank s6r-rank--${i + 1}`}>{i + 1}</span>
                <span className="s6r-top__name">{r.label}</span>
                <span className="s6r-top__num">{fmt(r[metric])}</span>
                <span className="s6r-top__track"><i style={{ width: `${(num(r[metric]) / max) * 100}%` }} /></span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SharePill({ value }) {
  if (value == null) return <span className="s6r-pill is-zero">—</span>;
  const cls = value >= 75 ? "is-high" : value >= 50 ? "is-mid" : value > 0 ? "is-low" : "is-zero";
  return <span className={`s6r-pill ${cls}`}>{`${value}%`}</span>;
}

function YesNo({ v }) {
  if (v !== "yes" && v !== "no") return <span className="s6r-muted">—</span>;
  return <span className={`s6r-yn ${v === "yes" ? "is-yes" : "is-no"}`}>{v === "yes" ? "Yes" : "No"}</span>;
}

function Thumb({ url, i, school }) {
  const [broken, setBroken] = useState(false);
  return (
    <a className="s6r-thumb" href={url} target="_blank" rel="noopener noreferrer" title={`Open photo ${i + 1} in a new tab`}>
      {broken ? <FaImage aria-hidden="true" /> : <img src={url} alt={`Geotagged upload ${i + 1} of ${school || "the school"}`} loading="lazy" onError={() => setBroken(true)} />}
    </a>
  );
}

function Pager({ page, total, pageSize, onPage, loading }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total ? (page - 1) * pageSize + 1 : 0;
  const to = Math.min(total, page * pageSize);
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) nums.push(p);
  return (
    <div className="s6r-pager">
      <span className="s6r-pager__info">{total ? `Showing ${fmt(from)}–${fmt(to)} of ${fmt(total)} schools` : "No schools"}</span>
      <div className="s6r-pager__btns">
        <button type="button" onClick={() => onPage(1)} disabled={loading || page <= 1} aria-label="First page"><FaAngleDoubleLeft /></button>
        <button type="button" onClick={() => onPage(page - 1)} disabled={loading || page <= 1} aria-label="Previous page"><FaChevronLeft /></button>
        {nums.map((p) => (
          <button key={p} type="button" className={p === page ? "is-on" : ""} onClick={() => onPage(p)} disabled={loading || p === page}>{p}</button>
        ))}
        <button type="button" onClick={() => onPage(page + 1)} disabled={loading || page >= pages} aria-label="Next page"><FaChevronRight /></button>
        <button type="button" onClick={() => onPage(pages)} disabled={loading || page >= pages} aria-label="Last page"><FaAngleDoubleRight /></button>
      </div>
    </div>
  );
}

const EMPTY_VIEW = { state: null, district: null, block: null, all: false };

// ─── page ─────────────────────────────────────────────────────────────────────
export default function Sc6Report() {
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const isStateAdmin = Number(user?.role) === 4;

  const [nat, setNat] = useState(null); // national snapshot
  const [st, setSt] = useState(null); // state snapshot (districts + blocks)
  const [view, setView] = useState(EMPTY_VIEW);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [exporting, setExporting] = useState("");
  const reportReq = useRef(0);

  const loadReport = useCallback(async (stateKey, { silent = false } = {}) => {
    const id = ++reportReq.current;
    setBusy(true);
    if (!silent) setError("");
    try {
      const d = await post("sc6/admin/report", stateKey ? { state: stateKey } : {});
      if (id !== reportReq.current) return;
      if (d.level === "state") {
        setSt({ ...d, requested: stateKey || null });
        if (d.state) setView((v) => (v.state ? v : { ...EMPTY_VIEW, state: { key: d.state.key, label: d.state.label } }));
        setError("");
      } else {
        setNat(d);
        if (stateKey) { // asked for a state, got India — go back rather than wait forever
          setView(EMPTY_VIEW);
          setError("No report was found for that State/UT/AB.");
        } else setError("");
      }
    } catch (e) {
      if (id !== reportReq.current) return;
      if (isAuthFailure(e)) setExpired(true);
      else setError(errText(e, "The report could not be loaded."));
    } finally {
      if (id === reportReq.current) setBusy(false);
    }
  }, []);

  useEffect(() => { loadReport(null); }, [loadReport]);

  const locked = isStateAdmin || !!st?.locked || !!nat?.locked;
  const level = view.block || view.all ? "schools" : view.district ? "district" : view.state || locked ? "state" : "national";
  const stateScope = view.state?.key || null;

  // The snapshot is a cached document — re-read it every 5 minutes while open.
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      loadReport(locked ? null : stateScope, { silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(t);
  }, [loadReport, locked, stateScope]);

  // the snapshot behind the current level
  const stReady = !!st && (!view.state || st.requested === view.state.key || st.state?.key === view.state.key);
  const report = level === "national" ? nat : stReady ? st : null;
  const refreshMin = report?.refreshMinutes || 10;
  const preparing = !!report && !report.generatedAt;

  const districtRow = useMemo(() => (stReady && view.district ? (st.rows || []).find((r) => r.key === view.district.key) || null : null), [st, stReady, view.district]);
  const districtBlocks = useMemo(() => (stReady && view.district ? (st.blocks || []).filter((b) => b.districtKey === view.district.key) : []), [st, stReady, view.district]);
  const blockRow = useMemo(() => (view.block ? districtBlocks.find((b) => b.key === view.block.key) || null : null), [districtBlocks, view.block]);

  // headline figures of the current level
  const k = (level === "national" || level === "state" ? report?.totals : view.block ? blockRow : districtRow) || null;
  const kLoading = !k;
  const kk = k || {};
  const S = num(kk.schools);

  const areaName = view.block ? `${view.block.label}, ${view.district?.label}` : view.district ? `${view.district.label}, ${view.state?.label || st?.state?.label || ""}` : view.state?.label || st?.state?.label || "India";
  const counts = level === "national"
    ? [{ label: "States/UTs/ABs reporting", short: "States/UTs/ABs", value: kk.states, icon: <FaMapMarkedAlt />, tone: "blue" },
      { label: "Districts reporting", short: "districts", value: kk.districts, icon: <FaMapMarkerAlt />, tone: "teal" },
      { label: "Blocks reporting", short: "blocks", value: kk.blocks, icon: <FaLayerGroup />, tone: "violet" }]
    : level === "state"
      ? [{ label: "Districts reporting", short: "districts", value: kk.districts, icon: <FaMapMarkerAlt />, tone: "teal" },
        { label: "Blocks reporting", short: "blocks", value: kk.blocks, icon: <FaLayerGroup />, tone: "violet" }]
      : !view.block
        ? [{ label: "Blocks reporting", short: "blocks", value: kk.blocks ?? districtBlocks.filter((b) => num(b.schools) > 0).length, icon: <FaLayerGroup />, tone: "violet" }]
        : [];

  // ── area table ──
  const tableLevel = level === "schools" ? null : level;
  const L = tableLevel ? LEVELS[tableLevel] : null;
  const cols = useMemo(() => colsFor(tableLevel), [tableLevel]);
  const allRows = useMemo(() => {
    if (level === "national") return nat?.rows || [];
    if (level === "state") return stReady ? st.rows || [] : [];
    if (level === "district") return districtBlocks;
    return [];
  }, [level, nat, st, stReady, districtBlocks]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "schools", dir: "desc" });
  const [topMetric, setTopMetric] = useState("schools");
  useEffect(() => { setQuery(""); setSort({ key: "schools", dir: "desc" }); }, [level, view.state, view.district]);
  const sortedAll = useMemo(() => sortRows(allRows, sort, cols), [allRows, sort, cols]);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? sortedAll.filter((r) => String(r.label || "").toLowerCase().includes(q)) : sortedAll;
  }, [sortedAll, query]);
  const foot = query.trim() ? sumRows(rows) : kk;
  const realCount = allRows.filter((r) => !isNone(r.key)).length;
  const toggleSort = (key) => setSort((cur) => (cur.key === key ? { key, dir: cur.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "label" ? "asc" : "desc" }));
  const sortIcon = (key) => (sort.key !== key ? <FaSort className="s6r-sorticon is-idle" /> : sort.dir === "asc" ? <FaSortUp className="s6r-sorticon" /> : <FaSortDown className="s6r-sorticon" />);
  const maxBar = useMemo(() => ({ schools: Math.max(1, ...allRows.map((r) => num(r.schools))), items: Math.max(1, ...allRows.map((r) => num(r.items))) }), [allRows]);

  // ── school-wise list (live) ──
  const [sq, setSq] = useState({ search: "", page: 1 });
  const [schools, setSchools] = useState({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE, loaded: false });
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState("");
  const schoolsReq = useRef(0);
  const resetSchools = () => {
    schoolsReq.current += 1; // drop any answer still on its way for the previous list
    setSq({ search: "", page: 1 });
    setSchools({ rows: [], total: 0, page: 1, pageSize: PAGE_SIZE, loaded: false });
    setSchoolsError("");
  };

  // ── navigation ──
  const openState = (r) => {
    if (locked || isNone(r.key)) return;
    setView({ ...EMPTY_VIEW, state: { key: r.key, label: r.label } });
    loadReport(r.key, { silent: st?.requested === r.key });
  };
  const openDistrict = (r) => { if (!isNone(r.key)) setView((v) => ({ ...v, district: { key: r.key, label: r.label }, block: null, all: false })); };
  const openBlock = (r) => {
    if (isNone(r.key)) return;
    resetSchools();
    setView((v) => ({ ...v, block: { key: r.key, label: r.label }, all: false }));
  };
  const openAllSchools = () => { resetSchools(); setView((v) => ({ ...v, block: null, all: true })); };
  const goIndia = () => { setView(EMPTY_VIEW); loadReport(null, { silent: !!nat }); };
  const goState = () => setView((v) => ({ ...v, district: null, block: null, all: false }));
  const goDistrict = () => setView((v) => ({ ...v, block: null, all: false }));
  const openRow = level === "national" ? openState : level === "state" ? openDistrict : openBlock;
  const canOpenRow = (r) => !isNone(r.key) && !(level === "national" && locked);
  const scrollToTable = () => { try { document.getElementById("s6r-table")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (e) { /* no-op */ } };

  const stateLabel = view.state?.label || st?.state?.label || "";
  const crumbs = [
    !locked && { label: "India", onClick: level !== "national" ? goIndia : null },
    (view.state || locked) && { label: stateLabel || "State", onClick: level !== "state" ? goState : null },
    view.district && { label: view.district.label, onClick: view.block || view.all ? goDistrict : null },
    view.block && { label: view.block.label, onClick: null },
    view.all && { label: "All schools", onClick: null },
  ].filter(Boolean);
  const back = level === "state" && !locked ? { label: "All States/UTs/ABs", go: goIndia }
    : level === "district" ? { label: `All districts of ${stateLabel}`, go: goState }
      : level === "schools" ? { label: `Blocks of ${view.district?.label || "the district"}`, go: goDistrict } : null;

  const schoolScope = useMemo(() => {
    const p = {};
    const sKey = view.state?.key || st?.state?.key;
    if (sKey) p.state = sKey;
    if (view.district) p.district = view.district.key;
    if (view.block) p.block = view.block.key;
    return p;
  }, [view, st]);
  const loadSchools = useCallback(async (scope, q) => {
    const id = ++schoolsReq.current;
    setSchoolsLoading(true); setSchoolsError("");
    try {
      const d = await post("sc6/admin/schools", { ...scope, page: q.page, pageSize: PAGE_SIZE, ...(q.search.trim() ? { search: q.search.trim() } : {}) });
      if (id !== schoolsReq.current) return;
      setSchools({ rows: d.rows || [], total: num(d.total), page: num(d.page) || q.page, pageSize: num(d.pageSize) || PAGE_SIZE, loaded: true });
    } catch (e) {
      if (id !== schoolsReq.current) return;
      if (isAuthFailure(e)) setExpired(true);
      else setSchoolsError(errText(e, "The school list could not be loaded."));
    } finally {
      if (id === schoolsReq.current) setSchoolsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (level !== "schools") return undefined;
    const t = setTimeout(() => loadSchools(schoolScope, sq), sq.search ? 380 : 0);
    return () => clearTimeout(t);
  }, [level, schoolScope, sq, loadSchools]);

  const refresh = () => {
    loadReport(locked ? null : stateScope, { silent: !!report });
    if (level === "schools") loadSchools(schoolScope, sq);
  };

  // ── downloads ──
  const asOfText = report?.generatedAt ? `Data as of ${fmtStamp(report.generatedAt)} (snapshot, refreshed every ${refreshMin} minutes)` : "Snapshot not computed yet";
  const fileBase = () => {
    const parts = level === "national" ? ["India"] : [stateLabel, view.district?.label, view.block?.label];
    return `National_Event_28Oct2026_SC6_Report_${parts.map(filePart).filter(Boolean).join("_") || "India"}`;
  };
  const areaSheetAoa = (lvl, list, totals) => {
    const lc = colsFor(lvl);
    const head = ["#", LEVELS[lvl].col, ...lc.map((c) => c.xl)];
    const body = list.map((r, i) => [i + 1, r.label, ...lc.map((c) => colValue(r, c) ?? "")]);
    const footRow = ["", `Total (all ${LEVELS[lvl].plural})`, ...lc.map((c) => colValue(totals, c) ?? "")];
    return [head, ...body, footRow];
  };
  // State level: districts in the order of the district table, blocks A–Z
  // inside each; blocks whose district is not in the list go last.
  const blockGroups = () => {
    const blocks = st?.blocks || [];
    const groups = sortedAll.map((d) => ({ d, blocks: blocks.filter((b) => b.districtKey === d.key).sort(byName) }));
    const known = new Set(sortedAll.map((d) => d.key));
    const orphans = blocks.filter((b) => !known.has(b.districtKey)).sort(byName);
    if (orphans.length) groups.push({ d: { key: "__none__", label: "District not recorded", ...sumRows(orphans) }, blocks: orphans });
    return groups.filter((g) => g.blocks.length);
  };
  const summarySheet = (wb) => {
    const aoa = [
      [`${SC6_NAME} — Report`], [SC6_TITLE], [`Area: ${areaName}`], [`Under ${SC6_CAMPAIGN} · event day: ${CAMPAIGN_DAY}`], [asOfText], [`Downloaded: ${fmtStamp(new Date())}`], [],
      ["Indicator", "Value", "% of schools"],
      ...summaryLines(kk, counts),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 52 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
  };

  const exportAreaExcel = () => {
    const wb = XLSX.utils.book_new();
    summarySheet(wb);
    const ws = XLSX.utils.aoa_to_sheet(areaSheetAoa(tableLevel, sortedAll, kk));
    ws["!cols"] = [{ wch: 5 }, { wch: 30 }, ...colsFor(tableLevel).map(() => ({ wch: 16 }))];
    XLSX.utils.book_append_sheet(wb, ws, LEVELS[tableLevel].sheet);
    if (level === "state") {
      const lc = colsFor("district");
      const out = [["#", "District", "Block", ...lc.map((c) => c.xl)]];
      let n = 0;
      blockGroups().forEach((g) => g.blocks.forEach((b) => { n += 1; out.push([n, g.d.label, b.label, ...lc.map((c) => colValue(b, c) ?? "")]); }));
      out.push(["", "Total", `${stateLabel} (all blocks)`, ...lc.map((c) => colValue(kk, c) ?? "")]);
      const wsB = XLSX.utils.aoa_to_sheet(out);
      wsB["!cols"] = [{ wch: 5 }, { wch: 26 }, { wch: 26 }, ...lc.map(() => ({ wch: 16 }))];
      XLSX.utils.book_append_sheet(wb, wsB, "Block-wise");
    }
    XLSX.writeFile(wb, `${fileBase()}_${dateStamp()}.xlsx`);
  };

  const pdfAreaTable = (doc, startY, lvl, list, totals, M) => {
    const lc = colsFor(lvl);
    doc.autoTable({
      ...TABLE_STYLE,
      startY,
      head: [["#", LEVELS[lvl].col, ...lc.map((c) => (c.share ? "%" : c.label))]],
      body: list.map((r, i) => [i + 1, safeName(r.label), ...lc.map((c) => (c.share ? (colValue(r, c) == null ? "-" : `${colValue(r, c)}%`) : fmt(colValue(r, c))))]),
      foot: [["", `Total (all ${LEVELS[lvl].plural})`, ...lc.map((c) => (c.share ? (colValue(totals, c) == null ? "-" : `${colValue(totals, c)}%`) : fmt(colValue(totals, c))))]],
      columnStyles: { 0: { cellWidth: 26, textColor: C.muted }, 1: { cellWidth: 150, fontStyle: "bold" }, ...Object.fromEntries(lc.map((_, i) => [i + 2, { halign: "right" }])) },
      didParseCell: (h) => { if (h.section === "head" && h.column.index >= 2) h.cell.styles.halign = "right"; if (h.section === "foot" && h.column.index >= 2) h.cell.styles.halign = "right"; },
      showFoot: "lastPage",
      margin: { left: M, right: M, top: 36, bottom: 34 },
    });
    return doc.lastAutoTable.finalY;
  };

  const exportAreaPdf = async () => {
    const poster = await loadPoster();
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
    const M = 32;
    let y = pdfHeader(doc, { area: areaName, asOf: asOfText, poster, M });
    y = pdfKpis(doc, y, kk, counts, M) + 26;
    y = pdfSectionTitle(doc, y, LEVELS[tableLevel].heading, `${fmt(realCount)} ${LEVELS[tableLevel].plural} with submissions. Percentages are shares of the schools that submitted in that area.`, M);
    pdfAreaTable(doc, y, tableLevel, sortedAll, kk, M);
    if (level === "state") {
      const lc = colsFor("district");
      const body = [];
      blockGroups().forEach((g) => {
        body.push([
          { content: `${safeName(g.d.label)}  (${g.blocks.length} block${g.blocks.length === 1 ? "" : "s"})`, colSpan: 2, styles: { fontStyle: "bold", fillColor: [234, 241, 253], textColor: C.blue } },
          ...lc.map((c) => ({ content: c.share ? (colValue(g.d, c) == null ? "-" : `${colValue(g.d, c)}%`) : fmt(colValue(g.d, c)), styles: { fontStyle: "bold", fillColor: [234, 241, 253], textColor: C.blue, halign: "right" } })),
        ]);
        g.blocks.forEach((b, i) => body.push([i + 1, safeName(b.label), ...lc.map((c) => (c.share ? (colValue(b, c) == null ? "-" : `${colValue(b, c)}%`) : fmt(colValue(b, c))))]));
      });
      doc.addPage();
      const y2 = pdfSectionTitle(doc, 44, `Block-wise report - ${safe(stateLabel)}`, "Grouped by district (blue rows are district totals). Blocks are listed A-Z inside each district.", M);
      doc.autoTable({
        ...TABLE_STYLE,
        startY: y2,
        head: [["#", "Block", ...lc.map((c) => (c.share ? "%" : c.label))]],
        body,
        foot: [["", `Total - ${safe(stateLabel)}`, ...lc.map((c) => (c.share ? (colValue(kk, c) == null ? "-" : `${colValue(kk, c)}%`) : fmt(colValue(kk, c))))]],
        columnStyles: { 0: { cellWidth: 26, textColor: C.muted }, 1: { cellWidth: 170 }, ...Object.fromEntries(lc.map((_, i) => [i + 2, { halign: "right" }])) },
        didParseCell: (h) => { if ((h.section === "head" || h.section === "foot") && h.column.index >= 2) h.cell.styles.halign = "right"; },
        showFoot: "lastPage",
        margin: { left: M, right: M, top: 36, bottom: 34 },
      });
    }
    pdfFooter(doc, M, areaName);
    doc.save(`${fileBase()}_${dateStamp()}.pdf`);
  };

  const fetchAllSchools = async () => {
    const d = await post("sc6/admin/schools", { ...schoolScope, page: 1, pageSize: 20000, export: true, ...(sq.search.trim() ? { search: sq.search.trim() } : {}) });
    return { rows: d.rows || [], total: num(d.total) || (d.rows || []).length };
  };
  const schoolTotals = (list) => ({
    schools: list.length,
    quizYes: list.filter((r) => r.quizInAssembly === "yes").length,
    quizNo: list.filter((r) => r.quizInAssembly === "no").length,
    items: list.reduce((a, r) => a + num(r.ewasteItemsCollected), 0),
    disposedYes: list.filter((r) => r.disposedToRecycler === "yes").length,
    disposedNo: list.filter((r) => r.disposedToRecycler === "no").length,
    students: list.reduce((a, r) => a + num(r.studentsParticipated), 0),
    photos: list.reduce((a, r) => a + (r.photos || []).length, 0),
  });
  const listNote = (n, total) => [
    `School list as of ${fmtStamp(new Date())} (live list - may include schools that submitted after the snapshot)`,
    ...(sq.search.trim() ? [`Search: "${sq.search.trim()}"`] : []),
    ...(total > n ? [`Showing the first ${fmt(n)} of ${fmt(total)} schools`] : []),
  ];

  const exportSchoolsExcel = async () => {
    const { rows: list, total } = await fetchAllSchools();
    const t = schoolTotals(list);
    const wb = XLSX.utils.book_new();
    const aoa = [
      [`${SC6_NAME} — Report (school-wise)`], [SC6_TITLE], [`Area: ${areaName}`], [`Under ${SC6_CAMPAIGN} · event day: ${CAMPAIGN_DAY}`], ...listNote(list.length, total).map((x) => [x]), [`Downloaded: ${fmtStamp(new Date())}`], [],
      ["Indicator", "Value", "% of schools"],
      ...summaryLines(t, []),
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(aoa);
    ws1["!cols"] = [{ wch: 52 }, { wch: 16 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws1, "Summary");
    const head = ["S.No", "UDISE Code", "School Name", "State/UT/AB", "District", "Block", SC6_QUESTIONS.quizInAssembly, SC6_QUESTIONS.ewasteItemsCollected, SC6_QUESTIONS.disposedToRecycler, SC6_QUESTIONS.studentsParticipated, "No. of photos", "Photo 1", "Photo 2", "Photo 3", "Submitted on", "Acknowledgement No."];
    const body = list.map((r, i) => [
      i + 1, String(r.udise || ""), r.school || "", r.state || "", r.district || "", r.block || "",
      yn(r.quizInAssembly), num(r.ewasteItemsCollected), yn(r.disposedToRecycler), num(r.studentsParticipated),
      (r.photos || []).length, r.photos?.[0] || "", r.photos?.[1] || "", r.photos?.[2] || "", fmtStamp(r.submittedAt), r.ackId || "",
    ]);
    const totalRow = ["", "", `Total (${fmt(t.schools)} schools)`, "", "", "", `Yes: ${t.quizYes}`, t.items, `Yes: ${t.disposedYes}`, t.students, t.photos, "", "", "", "", ""];
    const ws2 = XLSX.utils.aoa_to_sheet([head, ...body, totalRow]);
    ws2["!cols"] = [{ wch: 6 }, { wch: 14 }, { wch: 40 }, { wch: 18 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 16 }, { wch: 26 }, { wch: 16 }, { wch: 10 }, { wch: 40 }, { wch: 40 }, { wch: 40 }, { wch: 22 }, { wch: 22 }];
    XLSX.utils.book_append_sheet(wb, ws2, "School-wise");
    XLSX.writeFile(wb, `${fileBase()}_Schools_${dateStamp()}.xlsx`);
  };

  const exportSchoolsPdf = async () => {
    const [{ rows: list, total }, poster] = await Promise.all([fetchAllSchools(), loadPoster()]);
    await new Promise((r) => setTimeout(r, 30)); // let "Preparing…" paint before the long layout
    const t = schoolTotals(list);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
    const M = 28;
    let y = pdfHeader(doc, { area: areaName, asOf: listNote(list.length, total).join("   |   "), poster, M });
    y = pdfKpis(doc, y, t, [], M) + 26;
    y = pdfSectionTitle(doc, y, "School-wise report", `${fmt(list.length)} school${list.length === 1 ? "" : "s"} - ${safe(areaName)}`, M);
    doc.autoTable({
      ...TABLE_STYLE,
      startY: y,
      head: [["#", "School", "District / Block", "Quiz in assembly", "E-Waste items", "Disposed to recycler", "Students", "Photos", "Submitted on", "Ack No."]],
      body: list.map((r, i) => [
        i + 1, `${safeName(r.school)}\nUDISE ${r.udise || "-"}`, `${safeName(r.district)}\n${safeName(r.block)}`,
        yn(r.quizInAssembly), fmt(r.ewasteItemsCollected), yn(r.disposedToRecycler), fmt(r.studentsParticipated), fmt((r.photos || []).length), safe(fmtStamp(r.submittedAt)) || "-", safe(r.ackId) || "-",
      ]),
      foot: [["", `Total - ${fmt(t.schools)} schools`, "", `Yes ${fmt(t.quizYes)}`, fmt(t.items), `Yes ${fmt(t.disposedYes)}`, fmt(t.students), fmt(t.photos), "", ""]],
      styles: { ...TABLE_STYLE.styles, fontSize: 7.4, cellPadding: 3.5, valign: "top" },
      columnStyles: {
        0: { cellWidth: 26, textColor: C.muted }, 1: { cellWidth: 200 }, 2: { cellWidth: 120 }, 3: { cellWidth: 58, halign: "center" },
        4: { cellWidth: 56, halign: "right" }, 5: { cellWidth: 64, halign: "center" }, 6: { cellWidth: 50, halign: "right" }, 7: { cellWidth: 40, halign: "right" },
      },
      didParseCell: (h) => {
        if (h.section === "body" && (h.column.index === 3 || h.column.index === 5)) {
          if (h.cell.raw === "Yes") h.cell.styles.textColor = C.green;
          if (h.cell.raw === "No") h.cell.styles.textColor = [185, 28, 28];
        }
      },
      showFoot: "lastPage",
      margin: { left: M, right: M, top: 32, bottom: 34 },
    });
    pdfFooter(doc, M, `${areaName} - school-wise`);
    doc.save(`${fileBase()}_Schools_${dateStamp()}.pdf`);
  };

  const runExport = async (kind) => {
    if (exporting) return;
    setExporting(kind);
    await new Promise((r) => setTimeout(r, 20));
    try {
      if (level === "schools") await (kind === "excel" ? exportSchoolsExcel() : exportSchoolsPdf());
      else if (kind === "excel") exportAreaExcel();
      else await exportAreaPdf();
    } catch (e) {
      if (isAuthFailure(e)) setExpired(true);
      else setError(`The ${kind === "excel" ? "Excel" : "PDF"} download failed — ${errText(e, "please try again.")}`);
    } finally {
      setExporting("");
    }
  };
  const canExport = level === "schools" ? schools.total > 0 : !!report?.generatedAt && !!k;

  // ── render ──
  const levelTitle = level === "schools" ? "School-wise report" : L.heading;
  const levelDesc = level === "national"
    ? "Every State/UT/AB with a submission. Click a row to open its districts."
    : level === "state" ? `Districts of ${stateLabel}. Click a district to open its blocks.`
      : level === "district" ? `Blocks of ${view.district?.label}, ${stateLabel}. Click a block to see its schools.`
        : view.all ? `Every school of ${view.district?.label} district that submitted the report.` : `Every school of ${view.block?.label} block, ${view.district?.label} that submitted the report.`;
  const topTitle = level === "national" ? "Top 5 States/UTs/ABs" : level === "state" ? "Top 5 districts" : "Top 5 blocks";
  const updatedLine = expired ? "" : !report ? (busy ? "Loading the latest figures…" : "") : preparing
    ? `Report being prepared · refreshes every ${refreshMin} minutes`
    : `Updated ${fmtStamp(report.generatedAt)} · refreshes every ${refreshMin} minutes`;

  return (
    <div className="s6r-page">
      {/* ═══ header band ═══ */}
      <div className="s6r-band">
        <div className="s6r-wrap s6r-band__row">
          <div className="s6r-band__main">
            <span className="s6r-chip">{`Under ${SC6_CAMPAIGN}`}</span>
            <div className="s6r-title">{`${SC6_NAME} — Report`}</div>
            <div className="s6r-sub">{SC6_TITLE}</div>
            <div className="s6r-crumbs" role="navigation" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <React.Fragment key={`${c.label}-${i}`}>
                  {i > 0 && <FaChevronRight className="s6r-crumbs__sep" aria-hidden="true" />}
                  {c.onClick ? <button type="button" onClick={c.onClick}>{c.label}</button> : <span aria-current={i === crumbs.length - 1 ? "page" : undefined}>{c.label}</span>}
                </React.Fragment>
              ))}
              {locked && <span className="s6r-lock"><FaLock aria-hidden="true" />State admin view</span>}
            </div>
            {updatedLine ? (
              <div className="s6r-updated" title={report?.nextUpdateAt ? `Next snapshot around ${fmtTime(report.nextUpdateAt)}` : undefined}>
                <FaRegClock aria-hidden="true" />
                <span>{updatedLine}</span>
                {busy && report ? <FaSyncAlt className="s6r-spin" aria-label="Refreshing" /> : null}
              </div>
            ) : null}
            <div className="s6r-actions">
              <button type="button" className="s6r-btn s6r-btn--ghost" onClick={() => navigate("/admin/environment-day-dashboard")}><FaArrowLeft aria-hidden="true" />Back to Reports</button>
              <button type="button" className="s6r-btn s6r-btn--ghost" onClick={refresh} disabled={busy || schoolsLoading}><FaSyncAlt className={busy ? "s6r-spin" : ""} aria-hidden="true" />{busy ? "Refreshing…" : "Refresh"}</button>
              <button type="button" className="s6r-btn s6r-btn--excel" onClick={() => runExport("excel")} disabled={!canExport || !!exporting} title={level === "schools" ? "All schools of this list, every page" : undefined}><FaFileExcel aria-hidden="true" />{exporting === "excel" ? "Preparing…" : "Download Excel"}</button>
              <button type="button" className="s6r-btn s6r-btn--pdf" onClick={() => runExport("pdf")} disabled={!canExport || !!exporting} title={level === "schools" ? "All schools of this list, every page" : undefined}><FaFilePdf aria-hidden="true" />{exporting === "pdf" ? "Preparing…" : "Download PDF"}</button>
            </div>
          </div>
          <div className="s6r-band__side">
            <img className="s6r-poster" src={posterImg} alt={`${SC6_NAME} poster — ${SC6_TITLE}`} width="1280" height="720" />
          </div>
        </div>
      </div>

      <div className="s6r-wrap s6r-body">
        {expired ? (
          <div className="s6r-state s6r-state--warn">
            <span className="s6r-state__icon"><FaLock /></span>
            <div className="s6r-state__title">Your session has expired</div>
            <div className="s6r-state__text">Please sign in again to open the National Event report.</div>
            <button type="button" className="s6r-btn s6r-btn--primary" onClick={() => navigate("/admin/login")}>Go to login</button>
          </div>
        ) : (
          <>
            {error ? (
              <div className="s6r-alert" role="alert">
                <FaExclamationTriangle aria-hidden="true" />
                <span>{`${error}${report ? " Showing the figures loaded earlier." : ""}`}</span>
                <button type="button" className="s6r-btn s6r-btn--small" onClick={refresh} disabled={busy}>Retry</button>
              </div>
            ) : null}

            {preparing ? (
              <div className="s6r-state">
                <span className="s6r-state__icon"><FaHourglassHalf /></span>
                <div className="s6r-state__title">The report is being prepared</div>
                <div className="s6r-state__text">{`Figures are recomputed every ${refreshMin} minutes. This page checks again automatically every 5 minutes — or press Refresh.`}</div>
                {report?.nextUpdateAt ? <div className="s6r-state__hint">{`Next snapshot around ${fmtTime(report.nextUpdateAt)}`}</div> : null}
              </div>
            ) : (
              <>
                {/* ═══ headline figures ═══ */}
                <section className="s6r-kpis" aria-label="Headline figures">
                  <Kpi i={0} tone="blue" icon={<FaSchool />} label="Schools submitted" loading={kLoading} value={fmt(S)}
                    caption={level === "national" ? `in ${fmt(kk.states)} States/UTs/ABs` : level === "state" ? `in ${fmt(kk.districts)} districts of ${stateLabel}` : view.block ? `in ${view.block.label} block` : `in ${fmt(counts[0]?.value)} blocks of ${view.district?.label}`} />
                  <Kpi i={1} tone="green" icon={<FaChalkboardTeacher />} label="Quiz on E-Waste in morning assembly" loading={kLoading} value={fmt(kk.quizYes)} caption={S ? `${pctText(kk.quizYes, S)} of schools` : "No submissions yet"} share={pct(kk.quizYes, S)} />
                  <Kpi i={2} tone="orange" icon={<FaLaptop />} label="No. of E-Waste items collected" loading={kLoading} value={fmt(kk.items)} caption={S ? `avg ${fmt(avg(kk.items, S))} per school` : "No submissions yet"} />
                  <Kpi i={3} tone="teal" icon={<FaRecycle />} label="Disposed to authorised recycler/refurbisher" loading={kLoading} value={fmt(kk.disposedYes)} caption={S ? `${pctText(kk.disposedYes, S)} of schools` : "No submissions yet"} share={pct(kk.disposedYes, S)} />
                  <Kpi i={4} tone="violet" icon={<FaUserGraduate />} label="Students participated" loading={kLoading} value={fmt(kk.students)} caption={S ? `avg ${fmt(avg(kk.students, S))} per school` : "No submissions yet"} />
                </section>
                <div className="s6r-facts">
                  {counts.map((c) => (
                    <span key={c.label} className={`s6r-fact s6r-tone--${c.tone}`}><i aria-hidden="true">{c.icon}</i>{c.label}<b>{kLoading ? "…" : fmt(c.value)}</b></span>
                  ))}
                  <span className="s6r-fact s6r-tone--orange"><i aria-hidden="true"><FaCamera /></i>Photos uploaded<b>{kLoading ? "…" : fmt(kk.photos)}</b></span>
                  <span className="s6r-fact s6r-tone--green"><i aria-hidden="true"><FaMapMarkerAlt /></i>Showing<b>{level === "national" ? "All India" : areaName}</b></span>
                </div>

                {/* ═══ Yes / No splits + leaders ═══ */}
                <section className={`s6r-insights${level === "schools" ? " is-two" : ""}`}>
                  <SplitCard tone="green" icon={<FaChalkboardTeacher />} title="Quiz organised in morning assembly" question={SC6_QUESTIONS.quizInAssembly} yes={kk.quizYes} no={kk.quizNo} base={S} loading={kLoading}
                    extremes={level !== "schools" ? <Extremes rows={allRows} yesKey="quizYes" word={L.plural} /> : null} />
                  <SplitCard tone="teal" icon={<FaRecycle />} title="Handed over to authorised recycler" question={SC6_QUESTIONS.disposedToRecycler} yes={kk.disposedYes} no={kk.disposedNo} base={S} loading={kLoading}
                    extremes={level !== "schools" ? <Extremes rows={allRows} yesKey="disposedYes" word={L.plural} /> : null} />
                  {level !== "schools" && (
                    <TopCard title={topTitle} rows={allRows} metric={topMetric} setMetric={setTopMetric} onOpen={openRow} canOpen={!(level === "national" && locked)} loading={!report} />
                  )}
                </section>

                {/* ═══ area table / school list ═══ */}
                <section className="s6r-section" id="s6r-table">
                  <div className="s6r-section__head">
                    <div>
                      <div className="s6r-section__title"><i aria-hidden="true" />{levelTitle}</div>
                      <div className="s6r-section__desc">{levelDesc}</div>
                    </div>
                    <div className="s6r-section__tools">
                      {back ? <button type="button" className="s6r-btn s6r-btn--soft" onClick={back.go}><FaArrowLeft aria-hidden="true" />{back.label}</button> : null}
                      {level === "district" ? <button type="button" className="s6r-btn s6r-btn--blue" onClick={() => { openAllSchools(); scrollToTable(); }}><FaListUl aria-hidden="true" />View all schools of this district</button> : null}
                    </div>
                  </div>

                  {level !== "schools" ? (
                    <div className="s6r-tablecard">
                      <div className="s6r-toolbar">
                        <label className="s6r-search">
                          <FaSearch aria-hidden="true" />
                          <input type="search" placeholder={`Search ${level === "national" ? "States/UTs/ABs" : L.plural}…`} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={`Search ${L.plural}`} />
                        </label>
                        <span className="s6r-count">{`${fmt(realCount)} ${L.plural}`}</span>
                        <span className="s6r-toolbar__hint">Click a column heading to sort · percentages are shares of that area&apos;s schools</span>
                      </div>
                      {!report || !k ? (
                        busy || !error ? <div className="s6r-skel-rows">{Array.from({ length: 8 }, (_, i) => <div key={i} className="s6r-skel s6r-skel--row" />)}</div> : (
                          <div className="s6r-empty">
                            <span className="s6r-empty__icon"><FaExclamationTriangle /></span>
                            <b>The figures could not be loaded</b>
                            <button type="button" className="s6r-btn s6r-btn--soft s6r-btn--small" onClick={refresh}>Try again</button>
                          </div>
                        )
                      ) : S === 0 ? (
                        <div className="s6r-empty">
                          <span className="s6r-empty__icon"><FaInbox /></span>
                          <b>No school has submitted yet</b>
                          <span>{`Submissions appear here within ${refreshMin} minutes of a school filing its ${SC6_NAME} report.`}</span>
                        </div>
                      ) : rows.length === 0 ? (
                        <div className="s6r-empty"><span className="s6r-empty__icon"><FaSearch /></span><b>{`No ${L.plural} match “${query}”`}</b><button type="button" className="s6r-btn s6r-btn--soft s6r-btn--small" onClick={() => setQuery("")}>Clear search</button></div>
                      ) : (
                        <div className="s6r-table-wrap">
                          <table className="s6r-table">
                            <thead>
                              <tr>
                                <th className="c-rank">#</th>
                                <th className={`c-name is-sort${sort.key === "label" ? " is-active" : ""}`} onClick={() => toggleSort("label")} aria-sort={sort.key === "label" ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                                  <span className="s6r-th">{L.col}{sortIcon("label")}</span>
                                </th>
                                {cols.map((c) => (
                                  <th key={c.key} className={`is-sort${c.share ? " c-pct" : ""}${sort.key === c.key ? " is-active" : ""}`} onClick={() => toggleSort(c.key)} title={c.title || c.xl} aria-sort={sort.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                                    <span className="s6r-th">{c.label}{sortIcon(c.key)}</span>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => {
                                const open = canOpenRow(r);
                                return (
                                  <tr key={r.key || `row-${i}`} className={open ? "is-link" : ""} onClick={() => open && openRow(r)} title={open ? `Open ${r.label}` : isNone(r.key) ? "Location not recorded for these schools" : undefined}>
                                    <td className="c-rank">{i + 1}</td>
                                    <td className="c-name">
                                      {open ? (
                                        <button type="button" className="s6r-namebtn" onClick={(e) => { e.stopPropagation(); openRow(r); }}>{r.label}<FaChevronRight aria-hidden="true" /></button>
                                      ) : <span className={isNone(r.key) ? "s6r-none" : ""}>{r.label}</span>}
                                    </td>
                                    {cols.map((c) => (
                                      <td key={c.key} className={c.share ? "c-pct" : ""}>
                                        {c.share ? <SharePill value={colValue(r, c)} /> : c.bar ? (
                                          <span className={`s6r-cellbar s6r-cellbar--${c.bar}`}>
                                            <b>{fmt(r[c.key])}</b>
                                            <i><em style={{ width: `${(num(r[c.key]) / maxBar[c.key]) * 100}%` }} /></i>
                                          </span>
                                        ) : fmt(r[c.key])}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td className="c-rank" />
                                <td className="c-name">{query.trim() ? `Total (${fmt(rows.length)} shown)` : `Total (all ${L.plural})`}</td>
                                {cols.map((c) => (
                                  <td key={c.key} className={c.share ? "c-pct" : ""}>{c.share ? <SharePill value={colValue(foot, c)} /> : fmt(colValue(foot, c))}</td>
                                ))}
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="s6r-tablecard">
                      <div className="s6r-toolbar">
                        <label className="s6r-search s6r-search--wide">
                          <FaSearch aria-hidden="true" />
                          <input type="search" placeholder="UDISE code or part of the school name" value={sq.search} onChange={(e) => setSq({ search: e.target.value, page: 1 })} aria-label="Search schools" />
                        </label>
                        <span className="s6r-count">{schools.loaded ? `${fmt(schools.total)} schools` : "…"}</span>
                        <span className="s6r-toolbar__hint">Live list · 50 per page · photos open in a new tab</span>
                      </div>
                      {schoolsError ? (
                        <div className="s6r-alert s6r-alert--inline" role="alert"><FaExclamationTriangle aria-hidden="true" /><span>{schoolsError}</span><button type="button" className="s6r-btn s6r-btn--small" onClick={() => loadSchools(schoolScope, sq)}>Retry</button></div>
                      ) : null}
                      {!schools.loaded && schoolsLoading ? (
                        <div className="s6r-skel-rows">{Array.from({ length: 8 }, (_, i) => <div key={i} className="s6r-skel s6r-skel--row" />)}</div>
                      ) : schools.rows.length === 0 ? (
                        schoolsError ? null : (
                          <div className="s6r-empty">
                            <span className="s6r-empty__icon">{sq.search ? <FaSearch /> : <FaInbox />}</span>
                            <b>{sq.search ? `No school matches “${sq.search}”` : "No school has submitted yet"}</b>
                            <span>{sq.search ? "Try the 11-digit UDISE code or a shorter part of the name." : "Schools appear here as soon as they submit the report."}</span>
                          </div>
                        )
                      ) : (
                        <div className={`s6r-table-wrap${schoolsLoading ? " is-busy" : ""}`}>
                          <table className="s6r-table s6r-table--schools">
                            <thead>
                              <tr>
                                <th className="c-rank">#</th>
                                <th className="c-left">UDISE</th>
                                <th className="c-left">School</th>
                                <th className="c-left">Block</th>
                                <th className="c-mid">Quiz in assembly</th>
                                <th>E-Waste items</th>
                                <th className="c-mid">Disposed to recycler</th>
                                <th>Students</th>
                                <th className="c-left">Photos</th>
                                <th className="c-left c-date">Submitted on</th>
                                <th className="c-left">Ack No.</th>
                              </tr>
                            </thead>
                            <tbody>
                              {schools.rows.map((r, i) => (
                                <tr key={`${r.udise}-${i}`}>
                                  <td className="c-rank">{(schools.page - 1) * schools.pageSize + i + 1}</td>
                                  <td className="c-udise">{r.udise}</td>
                                  <td className="c-left c-school">{r.school || "—"}</td>
                                  <td className="c-left">{r.block || <span className="s6r-none">Not recorded</span>}</td>
                                  <td className="c-mid"><YesNo v={r.quizInAssembly} /></td>
                                  <td><b>{fmt(r.ewasteItemsCollected)}</b></td>
                                  <td className="c-mid"><YesNo v={r.disposedToRecycler} /></td>
                                  <td>{fmt(r.studentsParticipated)}</td>
                                  <td className="c-left">
                                    <span className="s6r-thumbs">
                                      {(r.photos || []).length ? r.photos.map((u, j) => <Thumb key={`${u}-${j}`} url={u} i={j} school={r.school} />) : <span className="s6r-muted">—</span>}
                                    </span>
                                  </td>
                                  <td className="c-left c-date">{r.submittedAt ? <>{fmtDay(r.submittedAt)}<small>{fmtTime(r.submittedAt)}</small></> : "—"}</td>
                                  <td className="c-left c-ack">{r.ackId || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {schools.total > 0 ? <Pager page={schools.page} total={schools.total} pageSize={schools.pageSize} loading={schoolsLoading} onPage={(p) => setSq((q) => ({ ...q, page: p }))} /> : null}
                    </div>
                  )}
                  {level === "schools" ? (
                    <div className="s6r-note"><FaInfoCircle aria-hidden="true" />{`The school list is live; the figures above come from the snapshot of ${fmtTime(report?.generatedAt) || "—"}, so a school that submitted in the last few minutes may appear here first.`}</div>
                  ) : null}
                </section>
              </>
            )}

          </>
        )}
      </div>
    </div>
  );
}
